import React, { useState, useEffect, useContext, useRef } from 'react';
import { AppContext } from '../../../util/AppContext';
import PropType from 'prop-types';
import { getConfigValue } from '../../../util/getConfigValue';
import { Chart as ChartJS, Title, Tooltip, Legend, CategoryScale, LinearScale, LineElement, PointElement, BarElement } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { constructChartData, downloadDivChart, getTimePeriodLabel, utilizationReportConstants, hasValuesGreaterThanZero, setChartYAxes, baseBarChartOptions, baseLineChartOptions } from './utlizationChart';
import { getLoginsByPDFAction } from '../../../API/UtilizationReportingDashboard';
import { useErrorBoundary } from "react-error-boundary";
import ReportInfoTooltip from "../ReportInfoTooltip";
import Button from 'react-bootstrap/Button';
import ButtonTooltip from '../../Tooltip';
import ChartDataLabels from "chartjs-plugin-datalabels";
import { createColorPaletteRegistry } from '../ColorRegistry';

LoginsByPDFActionAndProductChart.propTypes = {
    filterState: PropType.object,
    colorRegistry: PropType.object
}

function LoginsByPDFActionAndProductChart(props) {
    const {
        filterState,
        colorRegistry
    } = props;

    const { showBoundary } = useErrorBoundary();
    const [appState, setAppState] = useContext(AppContext);
    const barChartRef = useRef(null);
    const lineChartRef = useRef(null);
    const [data, setData] = useState({});
    const [barChartData, setBarChartData] = useState({});
    const [lineChartData, setLineChartData] = useState({});
    const [isLoading, setIsLoading] = useState(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [activeButton, setActiveButton] = useState(utilizationReportConstants.current);
    const [percentageChartAvailable, setPercentageChartAvailable] = useState(true);
    const [priorTimePeriodTabAvailable, setPriorTimePeriodTabAvailable] = useState(true);
    const [activeNumberOrPercentageTab, setActiveNumberOrPercentageTab] = useState(utilizationReportConstants.number);
    const tooltipTarget = useRef(null); 
    const percentageChartTooltipTarget = useRef(null); 
    const priorPeriodChartTooltipTarget = useRef(null); 
    const [showTooltip, setShowTooltip] = useState(false);
    const [showPriorPeriodTabTooltip, setShowPriorPeriodTabTooltip] = useState(false);
    const [maxYValue, setMaxYValue] = useState(null);
    const [yAxesStepIncrementValue, setYAxesStepIncrementValue] = useState(1);
    const chartEnabled = JSON.parse(getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.LoginsWithPDFAction?.Enabled, appState)?.Value === 'true') || false;
    const [chartColorRegistry] = useState(() => createColorPaletteRegistry(
        JSON.parse(getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.ColorPalette, appState)?.Value) || [],
        { storageKey: 'utilizationReportingDashboard:loginsByPdfAction:colorpalette' }
    ));

    ChartJS.register(Title, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ChartDataLabels);

    const barChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        layout: baseBarChartOptions.layout,
        plugins: {
            legend: baseBarChartOptions.legend,
            datalabels: baseBarChartOptions.datalabels,
            title: {
                text: getTimePeriodLabel(filterState.timePeriodId, activeButton, true),
                ...baseBarChartOptions.title
            },
            tooltip: baseBarChartOptions.tooltip,      
        },      
        scales: {
            x: baseBarChartOptions.scales.x,
            y: {
                max: maxYValue,
                ...baseBarChartOptions.scales.y
            }
        },
        resizeDelay:  200,  
    };

    const lineChartOptions = {       
        responsive: true,  
        maintainAspectRatio: false,
        layout: baseLineChartOptions.layout,
        plugins: {
            legend: baseLineChartOptions.legend,
            datalabels: baseLineChartOptions.datalabels,
            title: {
                text: getTimePeriodLabel(filterState.timePeriodId, activeButton, true),
                ...baseLineChartOptions.title
            },
            tooltip: baseLineChartOptions.tooltip,   
        },      
        scales: {
            x: baseLineChartOptions.scales.x,
            y: {
                max: 100,
                ...baseLineChartOptions.scales.y,
                ticks: baseLineChartOptions.scales.y.ticks  
            }
        },
        resizeDelay:  200,          
    };

    const getData = async () => {
        if(filterState == undefined || filterState?.selectedProductIds == null) {
            return;
        }

        setIsLoading(true);
        await getLoginsByPDFAction({
                "timeFilter": filterState.timePeriodId,
                "productIds": filterState.selectedProductIds,
                "includePriorPeriod": true
        })        
        .then((responseJson) => {
            setPercentageChartAvailable(hasValuesGreaterThanZero(responseJson.currentPeriod));            
            setData(responseJson);
            setChartData(responseJson.currentPeriod);       
        })
        .catch((err) => {
           showBoundary(err);
        })
        .finally(() => {
            setIsLoading(false);
        });
    }

    const toggleChartTab = (activeTab, selectedData) => {
        setPercentageChartAvailable(hasValuesGreaterThanZero(selectedData));        
        setChartData(selectedData);
        setActiveButton(activeTab); 
    }
    const toggleNumberPercentageTab = (activeTab) => {
        setActiveNumberOrPercentageTab(activeTab);    
        setPriorTimePeriodTabAvailable(activeTab == utilizationReportConstants.percentage ? hasValuesGreaterThanZero(data.priorPeriod) : true);
    }
    
        
    const setChartData = (selectedData) => {       
        const filteredDataSet = filterState.allProducts 
            ? selectedData.dataSets.filter(item => item.label === utilizationReportConstants.allProducts) 
            : selectedData.dataSets.filter(item => item.label !== utilizationReportConstants.allProducts);

        setBarChartData({labels: selectedData.labels, datasets: constructChartData(filteredDataSet, chartColorRegistry, appState)});           
        
        setLineChartData({labels: selectedData.labels, datasets: constructChartData(filteredDataSet.map(({label, data, totals }) => ({
            label,
            data: data.map((value, index) => (totals[index] ? (value / totals[index]) * 100 : 0 ).toFixed(2)),
            totals
        })), chartColorRegistry, appState)});

        setChartYAxes(filteredDataSet, setYAxesStepIncrementValue, setMaxYValue);
    }

    useEffect(() => {
        if (chartEnabled && appState?.config && !isLoading) {               
            getData(); 
            setActiveButton(utilizationReportConstants.current);           
        }
    }, [filterState.selectedProductIds, filterState.timePeriodId]);

    let content;
    if (isLoading === null || isLoading) {
        content =  (<div className='p-5'>{getConfigValue(appState.config.Common?.Loading, appState)?.Value}</div>);
    }
    else {
        content =  (
            <>
                <div id="loginsWithPDFAction-ChartsArea" className='chart-canvas-wrapper'>
                    <Bar className={`chart ${isCollapsed ? 'collapsed' : ''} ${activeNumberOrPercentageTab === utilizationReportConstants.number ? '' : "inactive" }`} ref={barChartRef} options={barChartOptions} data={barChartData} />
                    <Line className={`chart ${isCollapsed ? 'collapsed' : ''} ${activeNumberOrPercentageTab === utilizationReportConstants.percentage ? '' : "inactive" }`} ref={lineChartRef} options={lineChartOptions} data={lineChartData} />
                </div>
                <div className='reportButtons-container'>
                    <div className='reportButtonPair-container'>
                        <div className='wrapper'>
                        <Button
                            className={`button first ${activeNumberOrPercentageTab === utilizationReportConstants.number ? "active" : "inactive" }`}
                            onClick={() => { toggleNumberPercentageTab(utilizationReportConstants.number);}}
                        >
                            {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Number?.Text, appState)?.Value || null}
                        </Button> 
                        </div>
                        <div className='wrapper'
                            ref={percentageChartTooltipTarget} 
                            onMouseOver={() => setShowTooltip(!percentageChartAvailable)}
                            onFocus={() => setShowTooltip(!percentageChartAvailable)}
                            onMouseOut={() => setShowTooltip(false)}   
                            onBlur={() => setShowTooltip(false)}   
                        >              
                            <Button                               
                                disabled = {!percentageChartAvailable}
                                className={`button first ${activeNumberOrPercentageTab === utilizationReportConstants.percentage ? "active" : "inactive" }`}
                                onClick={() => { toggleNumberPercentageTab(utilizationReportConstants.percentage);}}                      
                            >
                                {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Percentage?.Text, appState)?.Value || null}                        
                            </Button>
                            <ButtonTooltip               
                                showTooltip={showTooltip}
                                text={`${getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Percentage?.Disabled?.ToolTip, appState)?.Value}`}                    
                                ref={percentageChartTooltipTarget}
                                index={0} />
                        </div>  
                    </div>
                    <Button 
                        className='reportTextButton' 
                        onClick={() => downloadDivChart('loginsWithPDFAction-ChartsArea', getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.LoginsWithPDFAction?.Title, appState)?.Value || null)}
                        >
                            {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Download?.Text, appState)?.Value || null} <i className={getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Download?.Icon, appState)?.Value || null}></i>
                    </Button>                   
                </div>
                <div className='reportTabButton-container'>
                    <div className='wrapper'
                        >
                        <Button 
                            className={`tab-button ${activeButton === utilizationReportConstants.current ? "active" : "inactive" }`}
                            onClick={() => { toggleChartTab(utilizationReportConstants.current, data.currentPeriod);}}
                            >
                                {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.CurrentSelection?.Text, appState)?.Value || null} ({filterState.timePeriodTitle})
                        </Button>
                        
                    </div>
                    <div className='wrapper'
                        ref={priorPeriodChartTooltipTarget} 
                        onMouseOver={() => setShowPriorPeriodTabTooltip(!priorTimePeriodTabAvailable)}
                        onFocus={() => setShowPriorPeriodTabTooltip(!priorTimePeriodTabAvailable)}
                        onMouseOut={() => setShowPriorPeriodTabTooltip(false)}   
                        onBlur={() => setShowPriorPeriodTabTooltip(false)} 
                    >
                        <Button 
                            disabled={!priorTimePeriodTabAvailable}
                            className={`tab-button ${activeButton === utilizationReportConstants.prior ? "active" : "inactive" }`}
                            onClick={() => { toggleChartTab(utilizationReportConstants.prior, data.priorPeriod);}}
                            >
                                {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.PriorTimePeriod?.Text, appState)?.Value || null}
                        </Button>
                        <ButtonTooltip               
                                showTooltip={showPriorPeriodTabTooltip}
                                text={`${getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.PriorTimePeriod?.Disabled?.ToolTip, appState)?.Value}`}                    
                                ref={priorPeriodChartTooltipTarget}
                                index={0} />
                    </div>
                </div>
            </>
        );
    }
    
    return (
        <>
        {chartEnabled &&
        <div className='chart-panel'>
            <div className='chart-header'>           
                <Button className={`chart-header-button ${isCollapsed ? 'collapsed' : ''}`} onClick={()=> {setIsCollapsed(!isCollapsed)}}>
                    {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.LoginsWithPDFAction?.Title, appState)?.Value || null}
                    <ReportInfoTooltip ref={tooltipTarget} text={getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.LoginsWithPDFAction?.ToolTip, appState )?.Value} />                        
                </Button>
            </div>
            <div className={`chart-container ${isCollapsed ? 'collapsed' : ''}`}>
                {content}
            </div>
        </div>
        }
        </>
    );
}

export default LoginsByPDFActionAndProductChart;